import { useEffect } from 'react';

import { findMaterialByAttributes } from '../materials.utils';
import { useMaterials } from './useMaterials';

import { defaultColorSort } from '~/core/constants/sortAndFilter';
import { FiltersBySection } from '~/core/enums';
import { MaterialModel } from '~/core/models';
import { createSelectedHook } from '~/core/utils';
import {
  sortColorsByTrending,
  updateListAndFilters,
} from '~/core/utils/sortAndFilter';
import { useAppDispatch } from '~/redux/hooks';
import { updateColorsIds } from '~/redux/slices/color';
import {
  selectRestorableMaterial,
  selectSelectedMaterial,
  updateSelectedMaterial,
} from '~/redux/slices/material';
import { updateFilters, updateSortBy } from '~/redux/slices/sortAndFilter';

const useMaterialsData = (): MaterialModel[] => {
  const { materials } = useMaterials();

  return materials;
};

export const selectDefaultMaterial = ({
  materials,
  selectedValue,
}: {
  materials: MaterialModel[];
  selectedValue: Partial<MaterialModel> | undefined;
}) =>
  findMaterialByAttributes({
    materials,
    description: selectedValue?.description,
  }) ?? materials[0];

export const useSelectedMaterial = () => {
  const { selectedValue: selectedMaterial } = createSelectedHook({
    selector: selectSelectedMaterial,
    restorableSelector: selectRestorableMaterial,
    apiQueryHook: useMaterialsData,
    selectRestorableValue: ({ apiHookResponse, restorableValue }) =>
      findMaterialByAttributes({
        materials: apiHookResponse,
        description: restorableValue?.description,
      }),
    shouldReturnDefault: ({ selectedValue, apiHookResponse }) =>
      !selectedValue ||
      !apiHookResponse.find((material) => material.id === selectedValue?.id),
    selectDefaultValue: ({ apiHookResponse, selectedValue }) =>
      selectDefaultMaterial({ materials: apiHookResponse, selectedValue }),
  })();

  const dispatch = useAppDispatch();

  const { materials } = useMaterials();

  useEffect(() => {
    if (materials.length > 0 && !selectedMaterial) {
      const defaultMaterial = materials[0];

      if (defaultMaterial) {
        dispatch(updateSelectedMaterial(defaultMaterial));
      }
    } else if (selectedMaterial) {
      const component = FiltersBySection.COLOR;
      const sortedColors = sortColorsByTrending(selectedMaterial.colors);
      const { filters, ids } = updateListAndFilters(sortedColors, {});
      const colorsById = sortedColors.reduce(
        (z, color) => ({ ...z, [color.productId]: color }),
        {}
      );
      const updatedMaterial =
        materials.find((m) => m.id === selectedMaterial?.id) ||
        selectedMaterial;

      dispatch(updateSelectedMaterial(updatedMaterial));
      dispatch(updateColorsIds({ byId: colorsById, usableIds: ids }));
      dispatch(updateFilters({ component, filters, selectedFilters: {} }));
      dispatch(updateSortBy({ component, sortBy: defaultColorSort }));
    }
  }, [selectedMaterial, materials, dispatch]);

  return selectedMaterial;
};
